import { useAuth } from "context/AuthContext";
import { FC } from "react";
import { ComplaintReception, ComplaintStatus } from "types";
import { DateView } from "utils/helper";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  UserCircle,
  Building,
} from "lucide-react"; // or any icon library

type Props = {
  complaint: ComplaintReception;
  setRefetch: any;
};

const ComplaintInformationTab: FC<Props> = ({ complaint, setRefetch }) => {
  const { loginData } = useAuth();
  const forwardedFrom = complaint?.forwarding_history?.find(
    (item) => item.forwarded_to === loginData?.org_unit,
  )?.forwarded_by;

  const clientInfoFields = [
    {
      label: "Full Name",
      value: complaint?.client_full_name ?? "-",
      icon: <User className="w-4 h-4" />,
    },
    {
      label: "Gender",
      value: complaint?.gender ?? "-",
      icon: <UserCircle className="w-4 h-4" />,
    },
    {
      label: "Nationality",
      value: complaint?.nationality ?? "-",
    },
    {
      label: "Phone Number",
      value: complaint?.phone_number ?? "-",
      icon: <Phone className="w-4 h-4" />,
    },
    {
      label: "Email",
      value: complaint?.email ?? "-",
      icon: <Mail className="w-4 h-4" />,
    },
    {
      label: "Region",
      value: complaint?.region ?? "-",
      icon: <MapPin className="w-4 h-4" />,
    },
    {
      label: "Zone/City",
      value: complaint?.zone_or_city ?? "-",
      icon: <MapPin className="w-4 h-4" />,
    },
    {
      label: "Sub City",
      value: complaint?.sub_city ?? "-",
      icon: <MapPin className="w-4 h-4" />,
    },
    {
      label: "Woreda",
      value: complaint?.woreda_or_kebele ?? "-",
      icon: <MapPin className="w-4 h-4" />,
    },
    {
      label: "Kebele",
      value: complaint?.kebele ?? "-",
      icon: <MapPin className="w-4 h-4" />,
    },
    {
      label: "House Number",
      value: complaint?.house_number ?? "-",
      icon: <MapPin className="w-4 h-4" />,
    },
    {
      label: "Service Place",
      value: loginData?.structure?.admin_unit?.unit_name ?? "-",
      icon: <Building className="w-4 h-4" />,
    },
    {
      label: "Incident Date",
      value: complaint?.incident_date ? DateView(complaint.incident_date) : "-",
      icon: <Calendar className="w-4 h-4" />,
    },
  ];

  return (
    <div className="flex-1 min-w-[350px] animate-fadeIn">
      <div className="bg-white rounded-xl shadow-lg p-6 space-y-8">
        {/* Header with Client ID */}
        {/* <div className="border-b pb-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Complaint Details
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Complete information about the complaint
              </p>
            </div>
            <div className="px-4 py-2 bg-red-50 rounded-lg border border-red-100">
              <span className="text-sm text-gray-600">Client ID:</span>
              <span className="ml-2 font-mono font-bold text-red-700">
                {complaint?.client_id || "N/A"}
              </span>
            </div>
          </div>
        </div> */}

        {/* Client Info */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-1 h-6 bg-red-600 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">
              Client Information
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {clientInfoFields.map((item) => (
              <div
                key={item.label}
                className="flex items-start p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100"
              >
                {item.icon && (
                  <div className="mr-3 mt-1 text-gray-400">{item.icon}</div>
                )}
                <div className="flex-1">
                  <dt className="text-sm font-medium text-gray-500 mb-1">
                    {item.label}
                  </dt>
                  <dd
                    className={`font-medium ${item.value === "-" ? "text-gray-400" : "text-gray-900"}`}
                  >
                    {item.value}
                  </dd>
                </div>
              </div>
            ))}

            {loginData?.org_unit !== complaint?.org_unit_id &&
              forwardedFrom && (
                <div className="flex items-start p-3 rounded-lg bg-amber-50 border border-amber-100 md:col-span-2">
                  <div className="mr-3 mt-1 text-amber-600">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <dt className="text-sm font-medium text-amber-700 mb-1">
                      Forwarded From
                    </dt>
                    <dd className="font-semibold text-amber-800">
                      {forwardedFrom}
                    </dd>
                    <p className="text-xs text-amber-600 mt-1">
                      This complaint was forwarded to your department
                    </p>
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Accused Info */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-1 h-6 bg-red-600 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">
              Accused Information
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 rounded-lg border border-gray-100">
              <dt className="text-sm font-medium text-gray-500 mb-1">
                Accused Person Name
              </dt>
              <dd
                className={`font-medium ${!complaint?.accused_person_name ? "text-gray-400" : "text-gray-900"}`}
              >
                {complaint?.accused_person_name || "Not specified"}
              </dd>
            </div>

            <div className="p-3 rounded-lg border border-gray-100">
              <dt className="text-sm font-medium text-gray-500 mb-1">
                Accused Department
              </dt>
              <dd
                className={`font-medium ${!complaint?.unit_name ? "text-gray-400" : "text-gray-900"}`}
              >
                {complaint?.unit_name || "Not specified"}
              </dd>
            </div>
          </div>
        </div>

        {/* Complaint Summary & Requested Resolution */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 bg-purple-600 rounded-full"></div>
                <h3 className="text-base font-semibold text-gray-900">
                  Complaint Summary
                </h3>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 min-h-[120px]">
                {complaint?.complaint_summary ? (
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                    {complaint.complaint_summary}
                  </p>
                ) : (
                  <p className="text-gray-400 italic">No summary provided</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 bg-green-600 rounded-full"></div>
                <h3 className="text-base font-semibold text-gray-900">
                  Requested Resolution
                </h3>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 min-h-[120px]">
                {complaint?.requested_resolution ? (
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                    {complaint.requested_resolution}
                  </p>
                ) : (
                  <p className="text-gray-400 italic">
                    No resolution requested
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            {complaint?.created_at && (
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Filed: {DateView(complaint.created_at)}</span>
              </div>
            )}
            {complaint?.status && (
              <div className="px-2 py-1 rounded-full bg-red-100 text-red-800 text-xs font-medium">
                Status: {ComplaintStatus[complaint.status]}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplaintInformationTab;
